// ============================================================================
// HOOK: useFinancialLedger.js — v1.0 — Passo 39 MC1
// Leitura em tempo real (onSnapshot) da coleção financial_ledger.
// Artigo 61.2: retorna dados normalizados para regime Caixa e Competência.
// ============================================================================

import { useEffect, useState } from 'react';
import { subscribeFinancialLedger } from '../services/FinancialLedgerRepository';
import { FIREBASE_CONFIG } from '../config/Config';
import { FINANCIAL_COLLECTION, TRANSACTION_STATUS, TRANSACTION_TYPES } from '../config/FinancialSchema';

const appId = FIREBASE_CONFIG.projectId;

// ── normalizeEntry ───────────────────────────────────────────────────────────
// Garante que entradas do Passo 38 (e legadas) seguem o schema V40.
function normalizeEntry(raw) {
  const isIncome = raw.type === TRANSACTION_TYPES.INCOME
    || raw.movementType === 'CREDIT'
    || raw.type === 'INCOME';

  const isPaid = raw.status === TRANSACTION_STATUS.PAID
    || raw.isPaid === true;

  // Artigo 61.1: safe amount — nunca float puro
  const rawAmt = raw.amount ?? raw.value ?? raw.amountCents ?? 0;
  const amount  = Math.round(Number(rawAmt) * 100) / 100;

  // Artigo 61.2: dualidade de datas
  const competenceDate = String(raw.competenceDate || raw.date || '').slice(0, 10);
  const paymentDate    = raw.paymentDate ? String(raw.paymentDate).slice(0, 10) : null;
  const dueDate        = raw.dueDate     ? String(raw.dueDate).slice(0, 10)     : null;

  // Auto-detect OVERDUE: pendente + dueDate no passado
  let status = raw.status || TRANSACTION_STATUS.PENDING;
  if (!isPaid && dueDate && dueDate < new Date().toISOString().slice(0, 10)) {
    if (status === TRANSACTION_STATUS.PENDING) status = TRANSACTION_STATUS.OVERDUE;
  }
  if (isPaid) status = TRANSACTION_STATUS.PAID;

  return {
    ...raw,
    type:            isIncome ? TRANSACTION_TYPES.INCOME : TRANSACTION_TYPES.EXPENSE,
    status,
    amount,
    amountCents:     Math.round(Number(rawAmt) * 100),
    competenceDate,
    paymentDate,
    dueDate,
    description:     String(raw.description || '').slice(0, 200),
    clientId:        raw.clientId        || null,
    processLabel:    raw.processLabel    || null,
    linkedWorkspace: raw.linkedWorkspace || null,
    pdfUrl:          raw.pdfUrl          || null,
    driveFileId:     raw.driveFileId     || null,
  };
}

// ── useFinancialLedger ────────────────────────────────────────────────────────
// Returns: { ledger, loading, error }
// ledger: normalized entries sorted by dueDate desc (then competenceDate desc)
export function useFinancialLedger() {
  const [ledger,  setLedger]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!appId) { setLoading(false); return; }

    const unsub = subscribeFinancialLedger(appId, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((r) => r.isDeleted !== true)
        .map(normalizeEntry)
        .sort((a, b) => {
          const da = a.dueDate || a.competenceDate || '';
          const db_ = b.dueDate || b.competenceDate || '';
          return db_.localeCompare(da);
        });
      setLedger(rows);
      setLoading(false);
      setError('');
    }, (e) => {
      setError(String(e?.message || 'Erro ao carregar ledger.'));
      setLoading(false);
    });

    return () => unsub();
  }, []);

  return { ledger, loading, error };
}
